import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import theme from '../theme';
import Navigation from './Navigation';
import TabBar from './tabs/TabBar';
import TabsKeepAlive from './tabs/TabsKeepAlive';
import AssistantDrawer from './assistant/AssistantDrawer';
import useSiteTabs from '../hooks/useSiteTabs';
import { normalizePath } from '../config/pageTitles';
import { isAppHost } from '../config/app-host';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@phosphor-icons/web/regular';
import '@phosphor-icons/web/fill';
import '../styles/tokens.css';

// Публичные страницы: анониму показываем без сайдбара и вкладок (чистый вид
// для внешних людей). Залогиненные получают обычный layout после маунта.
const PUBLIC_BARE_PATHS = ['/inout', '/supply-qr'];

// Preload AI Photo images once so they're in browser memory cache
const PRELOAD_IMAGES = [
  '/model-1.jpg', '/model-2.jpg', '/model-3.jpg', '/model-8.jpg',
  '/model-11.jpg', '/model-14.jpg', '/model-15.jpg', '/model-16.jpg',
  '/model-17.jpg', '/model-18.jpg', '/model-19.jpg', '/model-21.jpg',
  '/model-22.jpg', '/model-23.jpg', '/model-24.jpg',
  '/garden-wall.jpg', '/studio-wall.jpg', '/studio-vase.jpg',
  '/indoor-pool.jpg', '/outdoor-pool.jpg', '/sports-pool.jpg',
  '/greek-island.jpg', '/yacht.jpg', '/light-studio-wood.jpg', '/cyclorama.jpg',
  '/studio-brick.jpg', '/skyscraper.jpg', '/waterpark.jpg',
  '/concrete-studio.jpg', '/maldives-villa.jpg', '/tropical-beach.jpg', '/modern-pool.jpg',
];
let _preloaded = false;
const preloadImages = () => {
  if (_preloaded || typeof window === 'undefined') return;
  _preloaded = true;
  PRELOAD_IMAGES.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

const Layout = ({ children, location }) => {
  const { tabs, activePath, alivePaths, activateTab, closeTab } = useSiteTabs(location);
  // SSR и первый клиентский рендер детерминированы (только по location);
  // авторизацию проверяем после маунта — анониму без вспышки меню.
  const [anonBare, setAnonBare] = useState(() =>
    PUBLIC_BARE_PATHS.includes(normalizePath(location?.pathname))
  );
  useEffect(() => {
    const check = () => {
      const isPublic = PUBLIC_BARE_PATHS.includes(normalizePath(location?.pathname));
      // app.kluchik.io: до логина — только форма входа. Командное меню с «Прочим» (публичные
      // страницы команды) клиентам не показываем ни на одном маршруте.
      setAnonBare((isPublic || isAppHost()) && localStorage.getItem('analytics-auth') !== 'true');
    };
    check();
    window.addEventListener('auth-changed', check);
    return () => window.removeEventListener('auth-changed', check);
  }, [location?.pathname]);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const isCollapsed = localStorage.getItem('sidebar-collapsed') === 'true';
      return isCollapsed ? Navigation.SIDEBAR_COLLAPSED_WIDTH : Navigation.SIDEBAR_WIDTH;
    }
    return Navigation.SIDEBAR_WIDTH;
  });

  const handleSidebarToggle = useCallback(() => {
    const isCollapsed = localStorage.getItem('sidebar-collapsed') === 'true';
    setSidebarWidth(isCollapsed ? Navigation.SIDEBAR_COLLAPSED_WIDTH : Navigation.SIDEBAR_WIDTH);
  }, []);

  useEffect(() => {
    window.addEventListener('sidebar-toggle', handleSidebarToggle);
    // Синк на маунте: эффекты ребёнка (Navigation) бегут раньше родительских —
    // авто-коллапс на мобиле диспатчит sidebar-toggle до нашей подписки, и без
    // этого вызова margin остаётся от развёрнутого меню (белое поле у вкладок).
    handleSidebarToggle();
    return () => window.removeEventListener('sidebar-toggle', handleSidebarToggle);
  }, [handleSidebarToggle]);

  useEffect(() => {
    preloadImages();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {!anonBare && <Navigation location={location} />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            marginLeft: anonBare ? 0 : `${sidebarWidth}px`,
            transition: 'margin-left 0.2s ease',
            minHeight: '100vh',
            width: anonBare ? '100%' : `calc(100% - ${sidebarWidth}px)`,
            '@media print': {
              marginLeft: '0 !important',
              width: '100% !important',
            },
          }}
        >
          {!anonBare && (
            <TabBar tabs={tabs} activePath={activePath} onActivate={activateTab} onClose={closeTab} />
          )}
          <TabsKeepAlive
            activePath={activePath}
            alivePaths={alivePaths}
            tabPaths={tabs.map((t) => t.path)}
          >
            {children}
          </TabsKeepAlive>
        </Box>
      </Box>
      {!anonBare && <AssistantDrawer />}
    </ThemeProvider>
  );
};

export default Layout;
